import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { SkeletonList } from "../components/Skeleton";
import type { CoachDashboard as Dash } from "../api/types";

/**
 * Coach's dashboard — team health at a glance for admins: membership, roster
 * fill, and upcoming meets with RSVP counts + lineup readiness, plus quick
 * links to manage roster, lineups, and standings.
 */
export function CoachDashboard() {
  const teams = useAsync(() => api.listTeams(), []);
  const [teamId, setTeamId] = useState("");
  const [dash, setDash] = useState<Dash | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string>();
  const [calUrl, setCalUrl] = useState<string>();
  const [calCopied, setCalCopied] = useState(false);

  const shareCalendar = async () => {
    try {
      const res = await api.getTeamCalendarLink(teamId);
      if (res.httpsUrl) {
        setCalUrl(res.httpsUrl);
        try {
          await navigator.clipboard.writeText(res.httpsUrl);
          setCalCopied(true);
          setTimeout(() => setCalCopied(false), 1500);
        } catch {
          /* link shown below for manual copy */
        }
      }
    } catch {
      /* non-fatal */
    }
  };

  // Teams where the signed-in user is an admin (dashboard is admin-only).
  const adminTeams = (teams.data?.items ?? []).filter((t) => t.role === "admin");

  useEffect(() => {
    if (adminTeams.length > 0 && !teamId) setTeamId(adminTeams[0].teamId);
  }, [adminTeams, teamId]);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    setLoading(true);
    setErr(undefined);
    api
      .getCoachDashboard(teamId)
      .then((d) => !cancelled && setDash(d))
      .catch((e) => !cancelled && setErr((e as Error).message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

  return (
    <div>
      <div className="dash-banner row spread">
        <div className="row" style={{ gap: "0.6rem", alignItems: "center" }}>
          <img
            src="/media/crest-emblem.webp"
            alt=""
            className="team-crest"
            aria-hidden="true"
          />
          <h1 style={{ margin: 0 }}>Coach dashboard</h1>
        </div>
        {adminTeams.length > 1 && (
          <select
            style={{ width: "auto" }}
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
          >
            {adminTeams.map((t) => (
              <option key={t.teamId} value={t.teamId}>
                {t.name ?? t.teamId}
              </option>
            ))}
          </select>
        )}
      </div>

      {!teams.loading && adminTeams.length === 0 && (
        <p className="muted">
          You're not an admin of any team. The dashboard is for coaches/team
          admins. <Link to="/teams">Manage teams →</Link>
        </p>
      )}

      {(teams.loading || loading) && <SkeletonList count={3} variant="card" />}
      {err && <p className="error">{err}</p>}

      {dash && !loading && (
        <>
          <div className="team-summary">
            <div className="team-stat">
              <span className="team-stat-num">{dash.members.total}</span>
              <span className="muted">
                members · {dash.members.admins} admin / {dash.members.parents}{" "}
                parent
              </span>
            </div>
            <div className="team-stat">
              <span className="team-stat-num">
                {dash.roster.weightClasses
                  ? `${dash.roster.filledClasses}/${dash.roster.weightClasses}`
                  : "—"}
              </span>
              <span className="muted">weight classes filled</span>
            </div>
            <div className="team-stat">
              <span className="team-stat-num">{dash.roster.wrestlers}</span>
              <span className="muted">
                roster wrestlers
                {dash.roster.unlinked > 0
                  ? ` · ${dash.roster.unlinked} unlinked`
                  : ""}
              </span>
            </div>
            <div className="team-stat">
              <span className="team-stat-num">{dash.upcomingCount}</span>
              <span className="muted">upcoming meets</span>
            </div>
          </div>

          {/* Quick actions */}
          <div className="card">
            <div className="row" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
              <Link to={`/teams/${teamId}`}>
                <button className="secondary">Manage roster & members</button>
              </Link>
              <Link to="/standings">
                <button className="secondary">Team standings</button>
              </Link>
              <Link to="/meets/new">
                <button className="secondary">+ New meet</button>
              </Link>
              <button className="secondary" onClick={shareCalendar}>
                {calCopied ? "Link copied!" : "Share team calendar"}
              </button>
            </div>
            {calUrl && (
              <pre className="code-block" style={{ marginTop: "0.5rem" }}>
                {calUrl}
              </pre>
            )}
          </div>

          {/* Attention: unlinked wrestlers */}
          {dash.roster.unlinked > 0 && (
            <div className="card">
              <p style={{ margin: 0 }}>
                ⚠️ {dash.roster.unlinked} roster wrestler
                {dash.roster.unlinked === 1 ? " is" : "s are"} not linked to a
                record.{" "}
                <Link to={`/teams/${teamId}`} className="link-strong">
                  Link them →
                </Link>{" "}
                to show W–L on the roster.
              </p>
            </div>
          )}

          {/* Upcoming meets */}
          <div className="card">
            <h2>Upcoming meets</h2>
            {dash.upcoming.length === 0 && (
              <p className="muted">
                No upcoming meets.{" "}
                <Link to="/discover" className="link-strong">
                  Find some →
                </Link>
              </p>
            )}
            <div className="stagger">
              {dash.upcoming.map((m) => (
                <div key={m.meetId} className="dash-meet">
                  <div style={{ minWidth: 0 }}>
                    <Link to={`/meets/${m.meetId}`}>
                      <strong>{m.title}</strong>
                    </Link>
                    <div className="muted" style={{ fontSize: "0.85rem" }}>
                      {fmtDate(m.startAt)}
                      {m.format ? ` · ${m.format}` : ""}
                    </div>
                  </div>
                  <div className="row" style={{ gap: "0.35rem", flexWrap: "wrap" }}>
                    <span className="badge going">Going {m.going}</span>
                    <span className="badge">Maybe {m.maybe}</span>
                    {/dual/i.test(m.format ?? "") &&
                      (m.lineupSet ? (
                        <span className="badge accent">
                          Lineup {m.lineupCount}
                        </span>
                      ) : (
                        <span className="badge cancelled">No lineup</span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
